import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useValidateLogin } from '../../hooks/auth';
import { DASHBOARD_PATH_PREFIXES } from '../../utils/constants';

/**
 * Single top-level mount point that redirects a still-pending company straight to /payment as
 * soon as validate_login resolves, rather than waiting for the first dashboard API call to 402
 * — that 402 still fires as a fallback (see the response interceptor in src/api/queries/auth.js)
 * for any route this gate doesn't cover, but redirecting here avoids a flash of dashboard
 * content on a fresh app load.
 *
 * Scoped to the dashboard route-groups plus /profile specifically (ROAR-4900): /profile was
 * originally excluded on the assumption a company only reaches it after completing payment under
 * the normal signup flow — but pressing the browser Back button from the Stripe Embedded Checkout
 * page lands back on /signup, whose withAuthRoutes guard sees the already-set auth token and
 * immediately bounces to /profile, which is reachable well before payment. Left uncovered:
 * /connect_siemba, /pentest/*, which still only become reachable post-payment.
 */
const PROFILE_PATH = '/profile';

const PendingCompanyGate = () => {
  const { data } = useValidateLogin();
  const { pathname } = useLocation();

  const isOnGatedRoute =
    pathname === PROFILE_PATH || DASHBOARD_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix));
  const isCompanyPending = data?.success && data?.user?.company_status === 'pending';

  if (!isOnGatedRoute || !isCompanyPending) {
    return null;
  }

  return <Navigate to="/payment" replace />;
};

export default PendingCompanyGate;
